import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import Tabs from '../components/common/Tabs.jsx';
import ScatterChart from '../components/charts/ScatterChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import LineChart from '../components/charts/LineChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell, IdCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, formatSavingStr,
  buildBaseCostKpis, exportCSV, buildExportFilename,
  setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';

import MultiSelect from '../components/common/MultiSelect.jsx';

import RecommendationApiService from '../services/RecommendationApiService';
import InventoryApiService from '../services/InventoryApiService';
import APIConstants from '../services/APIConstants';
import DebugService from '../services/DebugService';
import { useAuth } from '../state/AuthContext';

const tabs = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'Azure Recommendations' },
];

// ── Recommendation Type Resolver ────────────────────────────
// Pill styling only — the recommendation type itself always comes from the backend.

const resolveDiskRec = (recType) => {
  const displayVal = (recType ?? '').toString().trim();
  const t = displayVal.toUpperCase();
  let recPillClass = 'rec-rs';
  if (t === '' || t === 'NA' || t === 'NONE' || t === 'OPTIMAL' || t === 'OPTIMIZED') recPillClass = 'rec-ok';
  else if (['DELETE', 'REMOVEUNUSEDRESOURCE', 'REMOVE'].includes(t)) recPillClass = 'rec-id';
  else if (['DOWNGRADE', 'SHRINK', 'RIGHTSIZE', 'CHANGEDISKSKU', 'CHANGESKU'].includes(t)) recPillClass = 'rec-rs';
  return { recType: displayVal || 'OPTIMAL', recPillClass };
};

const vmNameFromManagedBy = (item) => {
  if (item.isUnattached === true || !item.managedBy) return '— (unattached)';
  return item.managedBy.split('/').pop();
};

// ── Row Mappers ─────────────────────────────────────────────
// Every value below is read straight from the backend response; no recommendation
// types, savings, or states are computed on the frontend.

const mapCombinedRows = (data) =>
  data.map((item) => {
    const current = parseCost(item.currentMonthlyPriceValue);
    const recommended = parseCost(item.recommendedDiskMonthlyPriceValue);
    const saving = (item.savingCost && item.savingCost > 0) ? parseCost(item.savingCost) : (current - recommended);
    const { recType, recPillClass } = resolveDiskRec(item.recommendationType);

    return {
      id: item.id,
      diskName: item.diskName,
      location: item.location,
      resourceGroup: item.resourceGroup,
      sku: item.diskSku ?? '—',
      skuRaw: item.diskSku,
      tier: item.tier ?? '—',
      diskSizeGB: item.diskSizeGB,
      attachedVM: vmNameFromManagedBy(item),
      diskState: item.diskState ?? '—',
      isUnattached: item.isUnattached === true,
      iops: item.provisionedIOPS ?? null,
      cost: `$${current.toFixed(2)}`,
      costValue: current,
      recommendedCost: `$${recommended.toFixed(2)}`,
      recommendedCostValue: recommended,
      recommendedSku: item.recommendedSkuName ?? 'NA',
      recType,
      recPillClass,
      recLabel: item.recommendationDetail ?? '',
      recommendationRemark: item.remark ?? '',
      saving: formatSavingStr(saving),
      savingValue: saving,
      recommendedBy: item.recommendedBy ?? '—',
      actionLabel: 'Assist with AI',
      actionClass: 'mini-btn ai-assist',
      checked: false,
    };
  });

const mapCustomRows = (data) =>
  data.map((item) => {
    const current = parseCost(item.currentMonthlyPriceValue);
    const recommended = parseCost(item.recommendedDiskMonthlyPriceValue);
    const saving = (item.savingCost && item.savingCost > 0) ? parseCost(item.savingCost) : (current - recommended);
    const { recType, recPillClass } = resolveDiskRec(item.recommendationType);

    return {
      id: item.id,
      diskName: item.name,
      location: item.location,
      resourceGroup: item.resourceGroup,
      osType: item.osType ?? '—',
      sku: item.currentSkuName ?? '—',
      skuRaw: item.currentSkuName,
      tier: item.tier ?? '—',
      diskSizeGB: item.diskSizeGB,
      attachedVM: vmNameFromManagedBy(item),
      diskState: item.diskState ?? '—',
      isUnattached: item.isUnattached === true,
      iops: item.provisionedIOPS ?? null,
      cost: `$${current.toFixed(2)}`,
      costValue: current,
      recommendedCost: `$${recommended.toFixed(2)}`,
      recommendedCostValue: recommended,
      recommendedSku: item.recommendedSkuName ?? 'NA',
      recType,
      recPillClass,
      recLabel: item.recommendationDetail ?? '',
      recommendationRemark: item.remark ?? '',
      saving: formatSavingStr(saving),
      savingValue: saving,
      actionLabel: 'Assist with AI',
      actionClass: 'mini-btn ai-assist',
      checked: false,
    };
  });

const mapAzureRows = (data) =>
  data.map((item) => {
    const current = parseCost(item.currentMonthlyPriceValue);
    const recommended = parseCost(item.recommendedDiskMonthlyPriceValue ?? item.recommendedMonthlyPriceValue);
    const saving = (item.savingCost && item.savingCost > 0) ? parseCost(item.savingCost)
      : (item.savingsAmount && item.savingsAmount > 0) ? parseCost(item.savingsAmount)
        : (current - recommended);
    const { recType, recPillClass } = resolveDiskRec(item.recommendationType);

    return {
      id: item.id,
      diskName: item.name ?? item.diskName,
      location: item.location,
      resourceGroup: item.resourceGroup,
      sku: item.diskType ?? item.diskSku ?? '—',
      skuRaw: item.diskType ?? item.diskSku,
      tier: item.tier ?? '—',
      diskSizeGB: item.diskSizeGB,
      attachedVM: vmNameFromManagedBy(item),
      diskState: item.diskState ?? '—',
      isUnattached: item.isUnattached === true,
      iops: item.provisionedIOPS ?? null,
      cost: `$${current.toFixed(2)}`,
      costValue: current,
      recommendedCost: `$${recommended.toFixed(2)}`,
      recommendedCostValue: recommended,
      recommendedSku: item.recommendedSkuName ?? 'NA',
      recType,
      recPillClass,
      recLabel: item.recommendationDetail ?? '',
      recommendationRemark: item.remark ?? item.recommendationSubCategory ?? '',
      saving: formatSavingStr(saving),
      savingValue: saving,
      actionLabel: 'Assist with AI',
      actionClass: 'mini-btn ai-assist',
      checked: false,
    };
  });

// ── KPI Builder ─────────────────────────────────────────────
// Cost figures come from the backend totalMonthlyCost block; counts use the
// backend isUnattached flag.

const buildKpiData = (totalMonthlyCost, rows) => {
  const current = parseCost(totalMonthlyCost?.currentCost);
  const recommended = parseCost(totalMonthlyCost?.recommendedCost);
  const saving = parseCost(totalMonthlyCost?.savingCost);
  const unattachedRows = rows.filter((r) => r.isUnattached);
  const attachedCount = rows.length - unattachedRows.length;

  // 4th card left to the helper default: "Total Payable (after savings)" = recommendedCost
  return buildBaseCostKpis({
    countTitle: 'Total Disks',
    countValue: rows.length,
    countSubtitle: `${attachedCount} attached · ${unattachedRows.length} unattached`,
    current, saving, recommended,
  });
};

// ── Chart Builders ─────────────────────────────────────────

const buildScatterChartData = (rows) => {
  const withIops = rows.filter((r) => r.iops != null && r.diskSizeGB != null);
  if (!withIops.length) return null;

  const skuColors = {
    Premium_LRS: '#0078d4',
    StandardSSD_LRS: '#2ea66f',
    Standard_LRS: '#f57c00',
  };

  const skuGroups = {};
  withIops.forEach((r) => {
    const sku = r.skuRaw ?? r.sku ?? 'Unknown';
    if (!skuGroups[sku]) skuGroups[sku] = [];
    skuGroups[sku].push({ x: Number(r.diskSizeGB), y: Number(r.iops) });
  });

  return {
    datasets: Object.entries(skuGroups).map(([sku, points]) => ({
      label: sku,
      data: points,
      backgroundColor: skuColors[sku] ?? '#8e24aa',
      pointRadius: 6,
    })),
  };
};

const buildSkuDonutData = (rows) => {
  if (!rows.length) return null;
  const skuMap = {};
  rows.forEach((r) => {
    if (!r.costValue) return;
    const sku = r.sku && r.sku !== '—' ? r.sku : 'Unknown';
    skuMap[sku] = (skuMap[sku] || 0) + r.costValue;
  });
  const labels = Object.keys(skuMap);
  const total = labels.reduce((s, k) => s + skuMap[k], 0);
  if (!total) return null;
  const colors = ['#0078d4', '#2ea66f', '#f57c00', '#8e24aa', '#e53935'];
  return {
    labels,
    datasets: [{
      data: labels.map((k) => parseFloat(skuMap[k].toFixed(2))),
      backgroundColor: labels.map((_, i) => colors[i % colors.length]),
      borderWidth: 2,
      borderColor: '#fff',
    }],
  };
};

// Disk Size vs IOPS — mirrors the AWS EBS "Volume Size vs IOPS Utilization" design
// (dual-axis per-resource, top 20 by size). Managed disks expose provisioned IOPS
// (no utilization %), so we plot provisioned IOPS alongside size.
const buildSizeVsIopsChart = (rows) => {
  const active = rows
    .filter((r) => r.iops != null && r.diskSizeGB != null)
    .map((r) => ({ name: r.diskName || r.id || '—', iops: Number(r.iops) || 0, size: Number(r.diskSizeGB) || 0 }))
    .filter((r) => r.size > 0)
    .sort((a, b) => b.size - a.size)
    .slice(0, 20);
  if (!active.length) return null;
  return {
    labels: active.map((r) => (r.name.length > 16 ? r.name.slice(0, 15) + '…' : r.name)),
    datasets: [
      { label: 'IOPS provisioned', data: active.map((r) => r.iops), backgroundColor: 'rgba(33,150,243,0.7)', borderColor: '#2196f3', borderWidth: 1, borderRadius: 4, yAxisID: 'y1' },
      { label: 'Size (GiB)', data: active.map((r) => r.size), backgroundColor: 'rgba(245,124,0,0.7)', borderColor: '#f57c00', borderWidth: 1, borderRadius: 4, yAxisID: 'y2' },
    ],
  };
};

const buildSavingsByActionData = (rows) => {
  const actionMap = {};
  rows.forEach((r) => {
    if (r.savingValue <= 0) return;
    const t = (r.recType || 'Other').toUpperCase().trim();
    let label = 'Other';
    if (['DELETE', 'REMOVEUNUSEDRESOURCE', 'REMOVE'].includes(t)) label = 'Delete idle disks';
    else if (['DOWNGRADE', 'CHANGEDISKSKU', 'CHANGESKU'].includes(t)) label = 'Downgrade SKU';
    else if (['SHRINK', 'RIGHTSIZE'].includes(t)) label = 'Shrink / Rightsize';
    actionMap[label] = (actionMap[label] || 0) + r.savingValue;
  });
  const sorted = Object.entries(actionMap).sort((a, b) => b[1] - a[1]);
  if (!sorted.length) return null;
  const colorMap = {
    'Delete idle disks': '#ef4444',
    'Downgrade SKU': '#f57c00',
    'Shrink / Rightsize': '#2ea66f',
    'Other': '#9e9e9e',
  };
  return {
    labels: sorted.map(([l]) => l),
    datasets: [{
      data: sorted.map(([, v]) => parseFloat(v.toFixed(2))),
      backgroundColor: sorted.map(([l]) => colorMap[l] || '#9e9e9e'),
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

// Managed Disk Cost Distribution — monthly spend per disk (top 15), mirrors AWS EBS
const buildCostDistribution = (rows) => {
  const withCost = rows.filter((r) => r.costValue > 0).sort((a, b) => b.costValue - a.costValue).slice(0, 15);
  if (!withCost.length) return null;
  return {
    labels: withCost.map((r) => {
      const n = r.diskName || r.id || '—';
      return n.length > 18 ? n.slice(0, 17) + '…' : n;
    }),
    datasets: [{
      label: 'Cost ($)',
      data: withCost.map((r) => parseFloat(r.costValue.toFixed(2))),
      backgroundColor: '#0078d4',
      borderRadius: 4,
      barPercentage: 0.6,
    }],
  };
};

const buildLocationLineData = (rows) => {
  const locMap = {};
  rows.forEach((r) => {
    if (!r.costValue) return;
    locMap[r.location] = (locMap[r.location] || 0) + r.costValue;
  });
  const sorted = Object.entries(locMap).sort((a, b) => b[1] - a[1]).slice(0, 8);
  if (!sorted.reduce((s, [, v]) => s + v, 0)) return null;
  return {
    labels: sorted.map(([l]) => l),
    datasets: [{
      label: 'Monthly cost ($)',
      data: sorted.map(([, v]) => parseFloat(v.toFixed(2))),
      backgroundColor: 'rgba(0,120,212,0.7)',
      borderColor: '#0078d4',
      borderWidth: 1,
      borderRadius: 4,
    }],
  };
};

const buildInsightActions = (rows) => {
  const withSaving = rows
    .filter((r) => r.savingValue > 0)
    .sort((a, b) => b.savingValue - a.savingValue);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingValue, 0);
  const actions = withSaving.map((r, i) => {
    const t = (r.recType || '').toUpperCase().trim();
    let actionLabel = 'Review →';
    let actionClass = 'mini-btn';
    if (['DELETE', 'REMOVEUNUSEDRESOURCE', 'REMOVE'].includes(t)) { actionLabel = 'Delete →'; actionClass = 'mini-btn ghost'; }
    else if (['DOWNGRADE', 'CHANGEDISKSKU', 'CHANGESKU'].includes(t)) actionLabel = 'Downgrade →';
    else if (['SHRINK', 'RIGHTSIZE'].includes(t)) actionLabel = 'Shrink →';
    return {
      id: i + 1,
      title: r.diskName || r.id,
      amount: r.saving,
      desc: `${r.sku} · ${r.diskSizeGB} GiB · ${r.location}`,
      actionLabel,
      actionClass,
      row: r,
    };
  });
  return { actions, totalSaving };
};

// ── Table Columns ─────────────────────────────────────────

const diskColumns = [
  { key: 'id', label: 'ID', render: (row) => <IdCell row={row} /> },
  { key: 'diskName', label: 'Disk Name', minWidth: 140 },
  { key: 'location', label: 'Location' },
  { key: 'resourceGroup', label: 'Resource Group' },
  { key: 'sku', label: 'SKU' },
  { key: 'tier', label: 'Tier' },
  { key: 'diskSizeGB', label: 'Size (GiB)', className: 'num' },
  { key: 'attachedVM', label: 'Attached VM' },
  {
    key: 'diskState', label: 'Status',
    render: (row) => (
      <span className={`status-pill ${row.isUnattached ? 'st-stopped' : 'st-running'}`}>
        {row.diskState}
      </span>
    ),
  },
  { key: 'cost', label: 'Cost / Mo', className: 'num', render: (row) => <CostCell row={row} /> },
  { key: 'recommendationRemark', label: 'Recommendation', minWidth: 160, render: (row) => <RecCell row={row} /> },
  { key: 'saving', label: 'Saving / Mo', className: 'num', render: (row) => <SavingCell row={row} /> },
  { key: 'recommendedCost', label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
  {
    key: 'action', label: 'Action',
    render: (row) => (
      <button className={row.actionClass} onClick={row.onAction ? () => row.onAction(row) : undefined}>
        {row.actionLabel}
      </button>
    ),
  },
];

// ── Main Page ─────────────────────────────────────────────

const ManagedDisksOptimization = () => {
  const { selectedAccount, userDetails } = useAuth();

  const userId = userDetails?.userId || '1';

  const [activeTab, setActiveTab] = useState(0);

  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom, setIsLoadingCustom] = useState(false);
  const [isLoadingAzure, setIsLoadingAzure] = useState(false);

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows, setCustomRows] = useState([]);
  const [azureRows, setAzureRows] = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi, setCustomKpi] = useState([]);
  const [azureKpi, setAzureKpi] = useState([]);

  const [scatterData, setScatterData] = useState(null);
  const [sizeIopsData, setSizeIopsData] = useState(null);
  const [skuDonutData, setSkuDonutData] = useState(null);
  const [savingsByActionData, setSavingsByActionData] = useState(null);
  const [costDistData, setCostDistData] = useState(null);
  const [locationLineData, setLocationLineData] = useState(null);
  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal, setInsightTotal] = useState(0);

  const [tagList, setTagList] = useState([]);
  const [filterList, setFilterList] = useState({ tag: '', values: [] });

  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData, setAiData] = useState(null);

  const fetchedTabsRef = useRef(new Set());

  const handleDetailsClick = (row) => {
    setAiData({
      diskId: row.id, diskName: row.diskName, location: row.location,
      resourceGroup: row.resourceGroup, sku: row.sku, diskSizeGB: row.diskSizeGB,
      diskState: row.diskState, currentMonthlyCost: row.cost,
      recommendation: row.recType, recommendationDetail: row.recLabel,
      estimatedMonthlySaving: row.saving,
    });
    setAiPrompt(
      'You are a cloud cost advisor. Based on the data provided, give a short and natural insight ' +
      'as if you are speaking directly to the user — not reading a report. ' +
      'Mention the cost situation, what savings are within reach, and what the user should consider doing. ' +
      'Keep it conversational, friendly, and to the point. Do not mention field names or JSON.'
    );
    setAiOpen(true);
  };

  const fetchTagList = useCallback(() => {
    if (!selectedAccount) return;
    setTagList([]);
    setFilterList({ tag: '', values: [] });
    InventoryApiService.getAzureFilterTagList(APIConstants.ENDPOINT_AZURE_FILTER_TAG, { type: 'MANAGED_DISK', userAccountId: selectedAccount })
      .then((res) => setTagList(res?.data ?? []))
      .catch((err) => {
        DebugService.error('Managed Disks Optimization: Error fetching tag list', err);
      });
  }, [selectedAccount]);

  const fetchCustomData = useCallback((filters, isForceRefresh = false) => {
    if (!selectedAccount) return;
    setIsLoadingCustom(true);
    setCustomRows([]);
    const reqObj = {
      userId, type: 'CUSTOM_MANAGED_DISK', userAccountId: selectedAccount,
      filterKey: filters?.tag || undefined,
      filterValues: filters?.values?.length > 0 ? filters.values : undefined,
    };
    if (isForceRefresh) reqObj.isForceRefresh = true;
    RecommendationApiService.getAzureData(reqObj)
      .then((res) => {
        const rows = mapCustomRows(res?.data ?? []);
        setCustomRows(rows);
        setCustomKpi(buildKpiData(res?.totalMonthlyCost, rows));
      })
      .catch((error) => {
        DebugService.error('Managed Disks Optimization: Error fetching custom data', error);
      })
      .finally(() => setIsLoadingCustom(false));
  }, [selectedAccount, userId]);

  const fetchTabData = useCallback((tabIndex, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (tabIndex === 0 && isLoadingCombined) return;
    if (tabIndex === 1 && isLoadingCustom) return;
    if (tabIndex === 2 && isLoadingAzure) return;
    if (!isForceRefresh && fetchedTabsRef.current.has(tabIndex)) return;
    fetchedTabsRef.current.add(tabIndex);

    if (tabIndex === 0) {
      setIsLoadingCombined(true);
      setCombinedRows([]);
      const reqObj = { userId, type: 'COMBINED_MANAGED_DISK', userAccountId: selectedAccount };
      if (isForceRefresh) reqObj.isForceRefresh = true;
      RecommendationApiService.getAzureData(reqObj)
        .then((res) => {
          const rows = mapCombinedRows(res?.data ?? []);
          setCombinedRows(rows);
          setCombinedKpi(buildKpiData(res?.totalMonthlyCost, rows));
        })
        .catch((error) => {
          DebugService.error('Managed Disks Optimization: Error fetching combined data', error);
        })
        .finally(() => setIsLoadingCombined(false));
    }

    if (tabIndex === 1) {
      fetchCustomData(filterList, isForceRefresh);
    }

    if (tabIndex === 2) {
      setIsLoadingAzure(true);
      setAzureRows([]);
      const reqObj = { userId, type: 'MANAGED_DISK', userAccountId: selectedAccount };
      if (isForceRefresh) reqObj.isForceRefresh = true;
      RecommendationApiService.getAzureData(reqObj)
        .then((res) => {
          const rows = mapAzureRows(res?.data ?? []);
          setAzureRows(rows);
          setAzureKpi(buildKpiData(res?.totalMonthlyCost, rows));
        })
        .catch((error) => {
          DebugService.error('Managed Disks Optimization: Error fetching azure data', error);
        })
        .finally(() => setIsLoadingAzure(false));
    }
  }, [selectedAccount, userId, fetchCustomData, filterList, isLoadingCombined, isLoadingCustom, isLoadingAzure]);

  const handleTabChange = (i) => {
    setActiveTab(i);
    fetchTabData(i);
  };

  const handleRefresh = () => {
    if (isLoadingActive) return;
    fetchedTabsRef.current.delete(activeTab);
    fetchTabData(activeTab, true);
    fetchTagList();
  };

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  useEffect(() => {
    if (selectedAccount) {
      fetchedTabsRef.current = new Set();
      fetchTabData(activeTab);
      fetchTagList();
    } else {
      setIsLoadingCombined(false);
      setIsLoadingCustom(false);
      setIsLoadingAzure(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount || activeTab !== 1) return;
    if (filterList.tag === '' || filterList.values.length > 0) {
      fetchCustomData(filterList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList]);

  const activeRows = useMemo(() => {
    const rows = activeTab === 0 ? combinedRows : activeTab === 1 ? customRows : azureRows;
    return rows.map((row) => ({ ...row, onAction: handleDetailsClick }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, combinedRows, customRows, azureRows]);

  useEffect(() => {
    if (activeRows.length > 0) {
      setScatterData(buildScatterChartData(activeRows));
      setSizeIopsData(buildSizeVsIopsChart(activeRows));
      setCostDistData(buildCostDistribution(activeRows));
      setSkuDonutData(buildSkuDonutData(activeRows));
      setSavingsByActionData(buildSavingsByActionData(activeRows));
      setLocationLineData(buildLocationLineData(activeRows));
      const { actions, totalSaving } = buildInsightActions(activeRows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setScatterData(null); setSizeIopsData(null); setSkuDonutData(null); setSavingsByActionData(null); setCostDistData(null); setLocationLineData(null);
      setInsightActions([]); setInsightTotal(0);
    }
  }, [activeRows]);

  const activeKpi = activeTab === 0 ? combinedKpi : activeTab === 1 ? customKpi : azureKpi;
  const isLoadingActive = activeTab === 0 ? isLoadingCombined : activeTab === 1 ? isLoadingCustom : isLoadingAzure;

  // Keep AI chatbot context in sync with the data shown on this page
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Managed Disks Optimization',
      kpi: activeKpi,
      managedDiskRecommendations: activeRows,
    });
  }, [activeRows, activeKpi]);

  const handleExport = () => {
    const activeTabName = tabs[activeTab]?.label?.toLowerCase().replace(/\s+/g, '_') || 'unknown';
    exportCSV(
      activeRows,
      diskColumns.filter((c) => c.key !== 'action'),
      buildExportFilename(`managed_disks_optimization_${activeTabName}`, selectedAccount)
    );
  };

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Managed Disks</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Managed Disk Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{activeRows.length} disks</span>}
          </h1>
          <div className="meta">Delete unattached disks, downgrade over-provisioned SKUs, and right-size capacity</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport} disabled={activeRows.length === 0}>⬇ Export</button>
          <button className="btn ghost" onClick={handleRefresh} disabled={isLoadingActive}>↻ {isLoadingActive ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      <KpiRow kpiData={activeKpi} loading={isLoadingActive} />

      <div className="grid-3" style={{ marginTop: '14px' }}>
        <Card className="first" title="Disk Size vs IOPS Provisioned" subtitle="Provisioned IOPS vs size (GiB) · top 20 by size">
          <LineChart data={sizeIopsData} loading={isLoadingActive} y1Label="IOPS provisioned" y2Label="Size (GiB)" y1Suffix="" y2Prefix="" y1Max={null} />
        </Card>
        <Card className="first" title="Cost by disk SKU" subtitle="Premium / StandardSSD / Standard HDD">
          <DonutChart data={skuDonutData} loading={isLoadingActive} />
        </Card>
        <TopActionsCard
          title="Top disk actions"
          actions={insightActions}
          total={insightTotal}
          loading={isLoadingActive}
          entityName="disks"
          onAction={(item) => item.row && handleDetailsClick(item.row)}
        />
      </div>

      <Tabs tabs={tabs} defaultActive={0} onTabChange={handleTabChange} />

      {activeTab === 1 && (
        <div className="filter-bar">
          <div className="field">
            <label>Filter Tag</label>
            <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
              <option value="">None</option>
              {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Filter Tag Values</label>
            <MultiSelect
              options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
              value={filterList.values}
              onChange={(vals) => handleFilterChange('values', vals)}
              disabled={!filterList.tag}
            />
          </div>
          <div className="spacer" />
          <button className="btn ghost" onClick={() => setFilterList({ tag: '', values: [] })}>✕ Clear</button>
        </div>
      )}

      <Table
        rows={activeRows}
        columns={diskColumns}
        loading={isLoadingActive}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        emptyText="No managed disks found."
      />

      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Cost by location" subtitle="Monthly spend distribution by Azure region">
          <LineChart data={locationLineData} loading={isLoadingActive} />
        </Card>
        <Card className="first" title="Managed Disk Cost Distribution" subtitle="Monthly spend by disk (Top 15)">
          <HorizontalBarChart data={costDistData} loading={isLoadingActive} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default ManagedDisksOptimization;
